import { Box, Stack, Typography } from '@mui/material';
import { DICTIONARY } from 'Enum';
import { Dictionary } from 'Dictionary';
import { DocumentList } from '../DocumentList';
import { NoResults2 } from 'Components';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { NoDocumentsFound } from '../NoDocumentsFound';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IDocument, getDocuments as getDocumentsAPI } from 'API';
import { useLoader } from 'Hooks/useLoader';
import { compareAsc, compareDesc } from 'date-fns';
import { DocumentsSearchBar } from '../DocumentsSearchBar';

export const DESKTOP_DOCUMENTS_PAGINATION_LIMIT = 8;
export const MOBILE_DOCUMENTS_PAGINATION_LIMIT = 7;
export const MIN_SEARCH_QUERY = 1;
export const MIN_NEW_DATE = 4;

export enum DocumentsOrder {
  Newest = 'Newest',
  Oldest = 'Oldest',
}

export const DocumentsMain = () => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Documents);
  const { loader, isLoading, setIsLoading } = useLoader();
  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [query, setQuery] = useState<string>('');
  const [order, setOrder] = useState<DocumentsOrder>(DocumentsOrder.Oldest);

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  const initializeDocuments = useCallback(async () => {
    const documents = await getDocumentsAPI();
    if (documents) {
      setDocuments(documents);
      setIsLoading(false);
    }
  }, [setIsLoading]);

  const sortOldToNew = useCallback(
    (d1: IDocument, d2: IDocument) =>
      compareDesc(new Date(d1.createDate), new Date(d2.createDate)),
    []
  );

  const sortNewToOld = useCallback(
    (d1: IDocument, d2: IDocument) =>
      compareAsc(new Date(d1.createDate), new Date(d2.createDate)),
    []
  );

  const sortDocuments = useCallback(
    (documents: IDocument[], order: DocumentsOrder): IDocument[] => {
      if (order === DocumentsOrder.Oldest) {
        return [...documents].sort(sortOldToNew);
      } else {
        return [...documents].sort(sortNewToOld);
      }
    },
    [sortNewToOld, sortOldToNew]
  );

  const clearAutocompleteAndFilterTags = useCallback(() => {
    setSelectedSubjects([]);
    setQuery('');
  }, []);

  const clearSelectedSubjects = useCallback(() => setSelectedSubjects([]), []);

  const filterByQuery = useCallback(
    (documents: IDocument[], query: string): IDocument[] => {
      if (query.length < MIN_SEARCH_QUERY) return documents;

      return documents.filter((document) =>
        document.documentName.toLowerCase().includes(query.toLowerCase())
      );
    },
    []
  );

  const submitSearch = (value: string) => {
    clearSelectedSubjects();
    setQuery(value);
  };

  const filterBySubjects = useCallback(
    (
      documents: IDocument[],
      selectedSubjects: string[],
      documentsBySubjects: Map<string, IDocument[]>
    ) => {
      if (!selectedSubjects.length) return documents;

      return selectedSubjects.reduce(
        (acc, filter) => acc.concat(documentsBySubjects.get(filter)!),
        [] as IDocument[]
      );
    },
    []
  );

  const switchOrder = useCallback(() => {
    setOrder((prevOrder) =>
      prevOrder === DocumentsOrder.Newest
        ? DocumentsOrder.Oldest
        : DocumentsOrder.Newest
    );
  }, []);

  const documentsBySubjects = useMemo(() => {
    const documentsBySubjects = new Map<
      IDocument['subjectName'],
      IDocument[]
    >();

    documents.reduce((docsBySubjects, currentDocument) => {
      const subject = currentDocument.subjectName;

      if (docsBySubjects.has(subject)) {
        const accumulatedDocs = [
          ...docsBySubjects.get(subject)!,
          currentDocument,
        ];

        docsBySubjects.set(subject, accumulatedDocs);
      } else {
        docsBySubjects.set(subject, [currentDocument]);
      }

      return docsBySubjects;
    }, documentsBySubjects);

    return documentsBySubjects;
  }, [documents]);

  const subjects = useMemo(() => {
    if (!query) {
      return [...new Set(documents.map((document) => document.subjectName))];
    } else {
      const queriedDocuments = filterByQuery(documents, query);
      return [
        ...new Set(queriedDocuments.map((document) => document.subjectName)),
      ];
    }
  }, [documents, filterByQuery, query]);

  const anyFilterActive = useMemo(() => {
    return selectedSubjects.length > 0 || !!query;
  }, [query, selectedSubjects.length]);

  const processedDocuments = useMemo(() => {
    let processedDocuments = [...documents];

    if (selectedSubjects.length > 0) {
      processedDocuments = filterBySubjects(
        documents,
        selectedSubjects,
        documentsBySubjects
      );
      processedDocuments = filterByQuery(processedDocuments, query);
    } else {
      processedDocuments = filterByQuery(processedDocuments, query);
    }

    processedDocuments = sortDocuments(processedDocuments, order);

    return processedDocuments;
  }, [
    documents,
    documentsBySubjects,
    filterByQuery,
    filterBySubjects,
    order,
    query,
    selectedSubjects,
    sortDocuments,
  ]);

  useEffect(() => {
    initializeDocuments();
  }, [initializeDocuments, imposterId]);

  if (isLoading) {
    return loader;
  }

  if (documents?.length === 0 && !anyFilterActive) return <NoDocumentsFound />;

  return (
    <Stack gap={isDesktop ? '48px' : '40px'}>
      <Stack gap='40px'>
        <Stack gap={isDesktop ? '16px' : '8px'}>
          <Typography variant='h1' color='text.secondary' component='h1'>
            {dictionary.title}
          </Typography>
          <Typography variant='body3' color='text.secondary'>
            {dictionary.subTitle}
          </Typography>
        </Stack>
        <Stack gap={isDesktop ? '66px' : '56px'}>
          <DocumentsSearchBar
            onChange={submitSearch}
            options={[
              ...new Set(documents.map((document) => document.documentName)),
            ]}
          />
          {anyFilterActive && !documents.length && (
            <Box height={isDesktop ? '400px' : '180px'}>
              <NoResults2 />
            </Box>
          )}
        </Stack>
      </Stack>
      <Stack maxWidth='960px'>
        {documents.length > 0 && (
          <DocumentList
            documents={processedDocuments}
            subjects={subjects}
            selectedSubjects={selectedSubjects}
            setSelectedSubjects={setSelectedSubjects}
            order={order}
            switchOrder={switchOrder}
            paginationLimit={
              isDesktop
                ? DESKTOP_DOCUMENTS_PAGINATION_LIMIT
                : MOBILE_DOCUMENTS_PAGINATION_LIMIT
            }
            resetFilters={clearAutocompleteAndFilterTags}
            clearSelectedSubjects={clearSelectedSubjects}
          />
        )}
      </Stack>
    </Stack>
  );
};
